import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchIcon from '@material-ui/icons/Search';
import { Autocomplete as MuiAutocomplete } from '@material-ui/lab';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useNotify } from 'react-admin';
import debounce from 'lodash/debounce';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { ParsedPlace, parsePlaceResult } from './parsePlaceResult';
import { reportPlacesFetchFailure, useMapAvailability } from './MapProvider';

const SEARCH_IDLE_MS = 800;
const FETCH_FIELDS = [
  'addressComponents',
  'location',
  'formattedAddress',
] as const;

export type AddressAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (parsed: ParsedPlace) => void;
  onBeforeSelect?: (parsed: ParsedPlace) => boolean | Promise<boolean>;
  disabled?: boolean;
  label?: string;
};

type Suggestion = google.maps.places.AutocompleteSuggestion;

export const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelected,
  onBeforeSelect,
  disabled,
  label = 'Search Address',
}) => {
  const availability = useMapAvailability({ requireProbe: true });
  const places = useMapsLibrary('places');
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState<Suggestion[]>([]);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const requestIdRef = useRef(0);
  const unmountedRef = useRef(false);

  const onPlaceSelectedRef = useRef(onPlaceSelected);
  const onBeforeSelectRef = useRef(onBeforeSelect);
  const notifyRef = useRef(notify);
  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
    onBeforeSelectRef.current = onBeforeSelect;
    notifyRef.current = notify;
  }, [onPlaceSelected, onBeforeSelect, notify]);

  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  const ensureSessionToken = useCallback(() => {
    if (!places) return undefined;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, [places]);

  const fetchSuggestions = useMemo(
    () =>
      debounce(async (input: string) => {
        if (!places) return;
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        try {
          const { suggestions } =
            await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input,
              sessionToken: ensureSessionToken(),
              includedRegionCodes: ['us'],
              language: 'en-US',
            });
          if (unmountedRef.current || requestId !== requestIdRef.current) return;
          setOptions(suggestions.filter(s => s.placePrediction !== null));
        } catch (error) {
          reportPlacesFetchFailure(error);
          if (unmountedRef.current || requestId !== requestIdRef.current) return;
          setOptions([]);
        } finally {
          if (!unmountedRef.current && requestId === requestIdRef.current) {
            setSearching(false);
          }
        }
      }, SEARCH_IDLE_MS),
    [places, ensureSessionToken],
  );

  useEffect(() => () => fetchSuggestions.cancel(), [fetchSuggestions]);

  const handleInputChange = (
    _event: React.ChangeEvent<object>,
    newInput: string,
    reason: string,
  ) => {
    // 'reset' fires on selection; parent owns the post-select value.
    if (reason === 'reset') return;
    onChange(newInput);
    if (newInput.trim().length === 0) {
      fetchSuggestions.cancel();
      setOptions([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    fetchSuggestions(newInput);
  };

  const handleChange = async (
    _event: React.ChangeEvent<object>,
    newValue: string | Suggestion | null,
  ) => {
    if (!newValue || typeof newValue === 'string') return;
    const prediction = newValue.placePrediction;
    if (!prediction) return;

    fetchSuggestions.cancel();
    setSearching(false);
    setBusy(true);
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: [...FETCH_FIELDS] });
      // fetchFields ends the session; next typing burst needs a new token.
      sessionTokenRef.current = null;

      if (unmountedRef.current) return;

      if (!place.location) {
        notifyRef.current(
          'Address could not be located on the map — please try a different one.',
          'warning',
        );
        return;
      }

      const parsed = parsePlaceResult(place, prediction);

      const gate = onBeforeSelectRef.current;
      if (gate) {
        const proceed = await gate(parsed);
        if (unmountedRef.current || !proceed) return;
      }

      onPlaceSelectedRef.current(parsed);
    } catch (error) {
      reportPlacesFetchFailure(error);
      throw error;
    } finally {
      if (!unmountedRef.current) {
        setBusy(false);
        setOptions([]);
      }
    }
  };

  const unavailable = availability.status === 'unavailable';
  const loading = availability.status === 'loading';
  const effectiveDisabled = disabled || unavailable || loading;

  const helperText = unavailable ? 'Address search unavailable' : undefined;
  const placeholder = loading ? 'Loading search…' : undefined;
  const showSpinner = busy || searching || loading;

  return (
    <MuiAutocomplete<Suggestion, false, false, true>
      freeSolo
      fullWidth
      disabled={effectiveDisabled}
      options={options}
      filterOptions={x => x}
      getOptionLabel={option =>
        typeof option === 'string'
          ? option
          : option.placePrediction?.text.text ?? ''
      }
      inputValue={value}
      onInputChange={handleInputChange}
      onChange={handleChange}
      forcePopupIcon={false}
      renderOption={(option, { inputValue }) => {
        const text = option.placePrediction!.text.text;
        const parts = parse(
          text,
          match(text, inputValue, { findAllOccurrences: true }),
        );
        return (
          <span>
            {parts.map((part, index) =>
              part.highlight ? (
                <strong key={index}>{part.text}</strong>
              ) : (
                part.text
              ),
            )}
          </span>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                {showSpinner ? (
                  <CircularProgress
                    size={18}
                    role="progressbar"
                    aria-label="Searching address"
                    data-testid="search-address-loading"
                  />
                ) : (
                  <SearchIcon
                    color={effectiveDisabled ? 'disabled' : 'action'}
                  />
                )}
              </InputAdornment>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            'data-testid': 'search-address-input',
            'aria-busy': showSpinner,
          }}
        />
      )}
    />
  );
};
